import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { Group } from 'src/modules/groups/types';
import { Keyword } from 'src/modules/keywords/types';
import { AppointmentType, DefaultCustomField, MessageTemplate } from 'src/constants/types';
import {
  Campaign,
  CampaignPayloadParams,
  CampaignConfigGetResponse,
  CampaignConfigView,
  CampaignConfigsSearchResponse,
} from '../types';

export function useCampaignBuilderV2Api() {
  return useMemo(
    () => ({
      getCampaign: (campaignId: string, companyId: string, config?: CustomAxiosRequestConfig) =>
        apiClient.get<Campaign>(`/campaigns/${campaignId}`, { ...config, params: { companyId } }),
      listCampaigns: (companyId: string) =>
        apiClient.get<{ items: { name: string }[]; count: number }>(`/campaigns`, { params: { companyId } }),
      createCampaign: (params: CampaignPayloadParams, companyId: string) =>
        apiClient.post<Campaign[] | Campaign>(`/campaigns`, params, { params: { companyId } }),
      updateCampaign: (params: CampaignPayloadParams, campaignId: string, companyId: string) =>
        apiClient.put<Campaign[] | Campaign>(`/campaigns/${campaignId}`, params, { params: { companyId } }),
      activateCampaign: (campaignIds: string[], companyId: string) =>
        apiClient.put<void>(`/campaigns/activate`, campaignIds, { params: { companyId } }),
      getCampaignConfig: (id: string) =>
        apiClient.get<CampaignConfigGetResponse>(`/applications/campaign-configs/${id}`),
      getCampaignConfigByType: async (
        type: string,
        applicationId?: string,
      ): Promise<CampaignConfigView | null> => {
        const response = await apiClient.get<CampaignConfigsSearchResponse>(
          '/applications/campaign-configs',
          {
            params: {
              types: type,
              statuses: 'active',
              sort: 'date_created_desc',
              limit: 1,
              offset: 0,
              applicationIds: applicationId,
            },
          },
        );

        if (response.data.items.length > 0) {
          return response.data.items[0];
        }

        return null;
      },
      getGroups: (params: {
        companyId: string; limit?: number; offset?: number; statuses?: string; types?: string;
      }) => apiClient.get<{ items: Group[]; count: number }>(`/groups`, { params }),
      getKeywords: (params: { companyId: string; limit?: number; offset?: number }) =>
        apiClient.get<{ items: Keyword[]; count: number }>(`/keywords`, { params }),
      getMessageTemplates: (params: {
        companyId: string; limit?: number; offset?: number; sort?: string; status?: string;
      }) => apiClient.get<{ items: MessageTemplate[]; count: number }>(`/message-templates`, { params }),
      getAppointmentTypes: (params: {
        companyId: string; limit?: number; offset?: number; sort?: string; statuses?: string;
      }) => apiClient.get<{ items: AppointmentType[] }>(`/appointments/types`, { params }),
      // Merge-tag metadata: GET /templates with only { companyId, types }, the
      // same call useGetDefaultCustomFieldsApi makes (src/api/customFields).
      // `/templates/default-values` is a different endpoint — it returns the
      // per-company *values* of those fields, not the field definitions.
      getTemplateDefaultValues: (params: { companyId: string; types?: string }) =>
        apiClient.get<{count: number, items: DefaultCustomField[]}>(`/templates`, { params }),
      // Backend expects { texts: string[] } in one call. Sending { text } yields
      // '"texts" is required'.
      validateBlocklist: (data: { texts: string[] }) =>
        apiClient.post<{ matches?: Array<{ word: string }>; hasMatches?: boolean; error?: string; code?: number }>(
          `/blocklist/validate`,
          data,
        ),
    }),
    [],
  );
}
